/**
 * GrantPermissionModal Component
 *
 * Modal form for granting individual permissions to users with optional expiration.
 */

import React, { useState, useEffect } from 'react';
import {
  X,
  Shield,
  AlertCircle,
  Calendar,
  FileText,
  Info,
  CheckCircle,
} from 'lucide-react';
import {
  superAdminApi,
  AdminUser,
  RBACPermission,
  GrantPermissionData,
} from '../../services/adminApi';

interface GrantPermissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: AdminUser | null;
  currentPermissions: string[];
  onSubmit: (data: GrantPermissionData) => Promise<void>;
}

const CATEGORY_ICONS: Record<string, string> = {
  admin: '👤',
  analytics: '📊',
  api: '🔌',
  carriers: '🚗',
  crashes: '⚠️',
  drivers: '👷',
  fleet: '🚛',
  freight: '📦',
  inspections: '🔍',
  insurance: '🛡️',
  monitoring: '📡',
  shippers: '🏢',
  vins: '#️⃣',
  violations: '⛔',
};

export const GrantPermissionModal: React.FC<GrantPermissionModalProps> = ({
  isOpen,
  onClose,
  user,
  currentPermissions,
  onSubmit,
}) => {
  const [formData, setFormData] = useState<GrantPermissionData>({
    permission_key: '',
    reason: '',
    expires_at: undefined,
  });

  const [availablePermissions, setAvailablePermissions] = useState<Record<string, RBACPermission[]>>({});
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [selectedPermission, setSelectedPermission] = useState<RBACPermission | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [neverExpires, setNeverExpires] = useState(true);
  const [expirationDate, setExpirationDate] = useState('');

  // Fetch available permissions
  useEffect(() => {
    if (isOpen) {
      setLoadingPermissions(true);
      superAdminApi
        .getAllPermissionsGrouped()
        .then((grouped) => {
          setAvailablePermissions(grouped);
        })
        .catch((error) => {
          console.error('Failed to load permissions:', error);
        })
        .finally(() => {
          setLoadingPermissions(false);
        });
    }
  }, [isOpen]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        permission_key: '',
        reason: '',
        expires_at: undefined,
      });
      setSelectedPermission(null);
      setErrors({});
      setSubmitError(null);
      setNeverExpires(true);
      setExpirationDate('');
    }
  }, [isOpen]);

  // Find selected permission details
  useEffect(() => {
    if (formData.permission_key && availablePermissions) {
      let found: RBACPermission | null = null;
      Object.values(availablePermissions).forEach((permissions) => {
        const perm = permissions.find((p) => p.key === formData.permission_key);
        if (perm) {
          found = perm;
        }
      });
      setSelectedPermission(found);
    } else {
      setSelectedPermission(null);
    }
  }, [formData.permission_key, availablePermissions]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.permission_key) {
      newErrors.permission_key = 'Please select a permission';
    } else if (currentPermissions.includes(formData.permission_key)) {
      newErrors.permission_key = 'User already has this permission';
    }

    if (!formData.reason || formData.reason.trim().length < 10) {
      newErrors.reason = 'Reason must be at least 10 characters';
    }

    if (!neverExpires && expirationDate) {
      const expiry = new Date(expirationDate);
      const now = new Date();
      if (expiry <= now) {
        newErrors.expires_at = 'Expiration date must be in the future';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const submitData: GrantPermissionData = {
        permission_key: formData.permission_key,
        reason: formData.reason,
      };

      // Add expiration if set
      if (!neverExpires && expirationDate) {
        const expiry = new Date(expirationDate);
        expiry.setHours(23, 59, 59, 999); // Set to end of day
        submitData.expires_at = expiry.toISOString();
      }

      await onSubmit(submitData);
      onClose();
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Failed to grant permission');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePermissionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const permissionKey = e.target.value;
    setFormData((prev) => ({ ...prev, permission_key: permissionKey }));
    if (errors.permission_key) {
      setErrors((prev) => ({ ...prev, permission_key: '' }));
    }
  };

  const handleReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, reason: e.target.value }));
    if (errors.reason) {
      setErrors((prev) => ({ ...prev, reason: '' }));
    }
  };

  const handleExpirationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setExpirationDate(e.target.value);
    if (errors.expires_at) {
      setErrors((prev) => ({ ...prev, expires_at: '' }));
    }
  };

  // Get minimum date (tomorrow)
  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-2xl transform rounded-2xl bg-white shadow-xl transition-all">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-600" />
              Grant Permission to {user?.email}
            </h3>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6">
            {submitError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{submitError}</p>
              </div>
            )}

            <div className="space-y-5">
              {/* Permission Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Permission to Grant <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <select
                    value={formData.permission_key}
                    onChange={handlePermissionChange}
                    disabled={loadingPermissions}
                    className={`
                      w-full pl-10 pr-4 py-2.5 border rounded-lg text-sm
                      focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                      ${errors.permission_key ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'}
                      ${loadingPermissions ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                  >
                    <option value="">
                      {loadingPermissions ? 'Loading permissions...' : 'Select a permission...'}
                    </option>
                    {Object.entries(availablePermissions).map(([category, permissions]) => {
                      const icon = CATEGORY_ICONS[category.toLowerCase()] || '📋';
                      // Filter out permissions user already has
                      const availablePerms = permissions.filter(
                        (p) => !currentPermissions.includes(p.key)
                      );
                      if (availablePerms.length === 0) return null;

                      return (
                        <optgroup key={category} label={`${icon} ${category} (${availablePerms.length})`}>
                          {availablePerms.map((perm) => (
                            <option key={perm.key} value={perm.key}>
                              {perm.key} - {perm.description || 'No description'}
                            </option>
                          ))}
                        </optgroup>
                      );
                    })}
                  </select>
                </div>
                {errors.permission_key && (
                  <p className="mt-1 text-xs text-red-500">{errors.permission_key}</p>
                )}
              </div>

              {/* Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for Granting <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <FileText className="absolute left-3 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
                  <textarea
                    value={formData.reason}
                    onChange={handleReasonChange}
                    placeholder="Why is this permission being granted?"
                    rows={3}
                    className={`
                      w-full pl-10 pr-4 py-2.5 border rounded-lg text-sm resize-none
                      focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                      ${errors.reason ? 'border-red-300 bg-red-50' : 'border-gray-300'}
                    `}
                  />
                </div>
                <div className="flex items-center justify-between mt-1">
                  {errors.reason ? (
                    <p className="text-xs text-red-500">{errors.reason}</p>
                  ) : (
                    <p className="text-xs text-gray-500">Minimum 10 characters</p>
                  )}
                  <p className="text-xs text-gray-500">{formData.reason.length} characters</p>
                </div>
              </div>

              {/* Expiration */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Expiration (Optional)
                </label>
                <div className="space-y-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={neverExpires}
                      onChange={(e) => {
                        setNeverExpires(e.target.checked);
                        if (e.target.checked) {
                          setExpirationDate('');
                          setErrors((prev) => ({ ...prev, expires_at: '' }));
                        }
                      }}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Never expires</span>
                  </label>

                  {!neverExpires && (
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                      <input
                        type="date"
                        value={expirationDate}
                        onChange={handleExpirationChange}
                        min={getMinDate()}
                        className={`
                          w-full pl-10 pr-4 py-2.5 border rounded-lg text-sm
                          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                          ${errors.expires_at ? 'border-red-300 bg-red-50' : 'border-gray-300'}
                        `}
                      />
                    </div>
                  )}
                  {errors.expires_at && (
                    <p className="text-xs text-red-500">{errors.expires_at}</p>
                  )}
                </div>
              </div>

              {/* Permission Preview */}
              {selectedPermission && (
                <div className="border border-blue-200 bg-blue-50 rounded-lg p-4">
                  <div className="flex items-start gap-2 mb-2">
                    <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <h4 className="text-sm font-semibold text-blue-900">Permission Preview</h4>
                  </div>
                  <dl className="space-y-2 text-sm">
                    <div>
                      <dt className="text-blue-700 font-medium">Permission Key:</dt>
                      <dd className="text-blue-900 font-mono text-xs">{selectedPermission.key}</dd>
                    </div>
                    <div>
                      <dt className="text-blue-700 font-medium">Category:</dt>
                      <dd className="text-blue-900 capitalize flex items-center gap-1">
                        <span>{CATEGORY_ICONS[selectedPermission.category.toLowerCase()] || '📋'}</span>
                        {selectedPermission.category}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-blue-700 font-medium">Description:</dt>
                      <dd className="text-blue-900">
                        {selectedPermission.description || 'No description available'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-blue-700 font-medium">Expires:</dt>
                      <dd className="text-blue-900">
                        {neverExpires || !expirationDate ? (
                          'Never'
                        ) : (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(expirationDate)}
                          </span>
                        )}
                      </dd>
                    </div>
                  </dl>
                </div>
              )}

              {/* Info Message */}
              <div className="flex items-start gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <CheckCircle className="w-4 h-4 text-gray-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-gray-600">
                  This permission will be granted immediately and the user will have access until{' '}
                  {neverExpires || !expirationDate ? 'it is manually revoked' : 'the expiration date'}.
                  All permission changes are logged for audit purposes.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || loadingPermissions}
                className={`
                  flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors
                  ${
                    isSubmitting || loadingPermissions
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }
                `}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Granting...
                  </>
                ) : (
                  <>
                    <Shield className="w-4 h-4" />
                    Grant Permission
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
